/**
 * useNavFavorites — localStorage-backed pinned-nav state.
 *
 * Lets the user star nav items from the sidebar. Pinned items surface
 * at the top of the menu under a dedicated ★ Pinned section so the
 * paths a user actually lives in sit above the full hierarchy.
 *
 * Kept client-side (localStorage, keyed per user) for now — no backend
 * round-trip, no sync across devices. If that becomes a requirement,
 * mirror the same shape to a ``user_preferences`` collection server-side
 * and swap the storage driver behind this hook.
 */

import { useCallback, useEffect, useState } from 'react';

const STORAGE_PREFIX = 'plugd_nav_pinned_';

function storageKey(userId: string | null | undefined): string {
  return `${STORAGE_PREFIX}${userId || 'anon'}`;
}

function load(userId: string | null | undefined): string[] {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.filter((p): p is string => typeof p === 'string');
    }
    return [];
  } catch {
    return [];
  }
}

function persist(userId: string | null | undefined, paths: string[]): void {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(paths));
  } catch {
    // Storage unavailable (incognito quota, etc.) — silently accept.
  }
}

export interface UseNavFavoritesReturn {
  pinned: string[];
  isPinned: (path: string) => boolean;
  toggle: (path: string) => void;
  /** Reorder pinned items (drag-to-reorder hook-up for later). */
  reorder: (nextOrder: string[]) => void;
}

export function useNavFavorites(
  userId: string | null | undefined,
): UseNavFavoritesReturn {
  const [pinned, setPinned] = useState<string[]>(() => load(userId));

  // Reload when the user changes (e.g. after switching accounts).
  useEffect(() => {
    setPinned(load(userId));
  }, [userId]);

  // Persist on every change.
  useEffect(() => {
    persist(userId, pinned);
  }, [userId, pinned]);

  const isPinned = useCallback(
    (path: string) => pinned.includes(path),
    [pinned],
  );

  const toggle = useCallback((path: string) => {
    setPinned((prev) =>
      prev.includes(path) ? prev.filter((p) => p !== path) : [...prev, path],
    );
  }, []);

  const reorder = useCallback((nextOrder: string[]) => {
    // Filter to known paths only — keeps the array sane if a stale
    // entry sneaks in from a deprecated route.
    setPinned((prev) => nextOrder.filter((p) => prev.includes(p)));
  }, []);

  return { pinned, isPinned, toggle, reorder };
}
